import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '@/api/queryKeys';
import {
  createClassCategory,
  type CreateClassCategoryRequest,
  type CreatedClassCategory,
} from '@/features/class/boardsApi';

/**
 * POST /midl/api/v3/class/category — creates a new profile class category.
 *
 * Wired from the "Add category" action in the Manage categories popup.
 *
 * On success we invalidate the `class/category` query so the popup refetches
 * the authoritative category list (the new, still-empty category included).
 * We intentionally do NOT touch `authStore.rawUser.boardWithCategory` here:
 * the profile "Your classes" section is driven by that login payload and must
 * only ever show categories that contain classes — injecting an empty group
 * there previously leaked the brand-new category into "Your classes".
 */
export function useCreateClassCategoryMutation() {
  const queryClient = useQueryClient();

  return useMutation<CreatedClassCategory | null, unknown, CreateClassCategoryRequest>({
    mutationKey: ['class', 'category', 'create'],
    mutationFn: (vars) => createClassCategory(vars),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.class.categories });
      void queryClient.invalidateQueries({ queryKey: ['boards', 'joined'] });
    },
  });
}
